// frontend/src/components/LeadTable.jsx
// Optimized to batch email history requests to prevent rate limiting
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { US_STATES } from '../constants';
import { getLeadEmailHistory } from '../api';
import { Badge } from '../components/ui/badge';
import EmailHistoryModal from './EmailHistoryModal';
import { Mail, Info } from 'lucide-react';

// Global cache for email statuses
const emailStatusCache = new Map();
const STATUS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Request tracking to prevent duplicate requests
const pendingStatusRequests = new Map();
const STATUS_DEBOUNCE_TIME = 2000; // 2 seconds

const LeadTable = ({ leads = [], onEdit, onDelete, onBulkDelete, selectedLeads = [], onSelectLead }) => {
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [emailStatuses, setEmailStatuses] = useState({});
  const [selectedEmailLead, setSelectedEmailLead] = useState(null);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const itemsPerPage = 20;
  const componentMounted = useRef(true);
  const lastFetchTime = useRef(0);
  const fetchTimeoutRef = useRef(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      componentMounted.current = false;
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  // Make sure leads is an array before filtering
  const safeLeads = useMemo(() => Array.isArray(leads) ? leads : [], [leads]);

  // Filter leads with useMemo to prevent unnecessary recalculations
  const filteredLeads = useMemo(() => {
    if (!searchQuery) return safeLeads;
    const query = searchQuery.toLowerCase();
    return safeLeads.filter(lead => (
      (lead.name && lead.name.toLowerCase().includes(query)) ||
      (lead.email && lead.email.toLowerCase().includes(query)) ||
      (lead.phone && lead.phone.toLowerCase().includes(query)) ||
      (lead.state && lead.state.toLowerCase().includes(query)) ||
      (lead.city && lead.city.toLowerCase().includes(query)) ||
      (lead.id && lead.id.toString().includes(query))
    ));
  }, [safeLeads, searchQuery]);

  // Helper function to get the lead name
  const getLeadName = useCallback((leadId) => {
    const lead = leads.find(lead => lead.id === leadId);
    return lead ? lead.name : '';
  }, [leads]);

  // ===== OPTIMIZED EMAIL STATUS FETCHING =====
  // Instead of fetching every email status individually, batch them with a delay
  useEffect(() => {
    const fetchEmailStatuses = async () => {
      if (!filteredLeads.length) return;
      
      // Clear any existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      
      // Prevent rapid refetching (debounce)
      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchTime.current;
      if (timeSinceLastFetch < STATUS_DEBOUNCE_TIME) {
        console.log(`Debouncing email status fetch`);
        fetchTimeoutRef.current = setTimeout(() => {
          fetchEmailStatuses();
        }, STATUS_DEBOUNCE_TIME - timeSinceLastFetch);
        return;
      }
      
      if (componentMounted.current) {
        setIsLoadingEmails(true);
      }
      lastFetchTime.current = now;
      
      try {
        // Get only the leads that need status updates
        const leadsToFetch = filteredLeads.filter(lead => {
          if (!lead.id) return false;
          
          // Check if we have cached data that's still valid
          const cachedData = emailStatusCache.get(lead.id);
          const isCacheValid = cachedData && (now - cachedData.timestamp < STATUS_CACHE_DURATION);
          
          // Check if there's already a pending request for this lead
          const isPending = pendingStatusRequests.has(lead.id);
          
          return !isCacheValid && !isPending;
        });
        
        console.log(`Fetching email statuses for ${leadsToFetch.length} leads`);
        
        // Process leads in batches to avoid overwhelming the server
        const batchSize = 5;
        for (let i = 0; i < leadsToFetch.length; i += batchSize) {
          const batch = leadsToFetch.slice(i, i + batchSize);
          
          // Process each lead in the batch
          for (const lead of batch) {
            if (!componentMounted.current) return;
            
            // Mark this request as pending
            pendingStatusRequests.set(lead.id, true);
            
            try {
              const response = await getLeadEmailHistory(lead.id);
              
              if (componentMounted.current && response.success && response.data && response.data.length > 0) {
                // Sort by date (newest first)
                const sortedEmails = response.data.sort((a, b) => 
                  new Date(b.sent_at) - new Date(a.sent_at)
                );
                
                // Get all initial notifications
                const initialEmails = sortedEmails.filter(
                  email => email.email_type === 'initial_notification'
                );
                
                // Get all full details emails
                const fullDetailsEmails = sortedEmails.filter(
                  email => email.email_type === 'full_details'
                );
                
                const statusData = {
                  count: sortedEmails.length,
                  initialCount: initialEmails.length,
                  initialDate: initialEmails.length > 0 ? initialEmails[0].sent_at : null,
                  fullCount: fullDetailsEmails.length,
                  fullDate: fullDetailsEmails.length > 0 ? fullDetailsEmails[0].sent_at : null,
                  status: sortedEmails[0].status || 'sent',
                  date: sortedEmails[0].sent_at,
                  purchased: lead.is_purchased,
                  // Calculate how many unique agents received the initial email
                  uniqueAgents: [...new Set(initialEmails.map(email => email.agent_email))].length,
                  timestamp: now
                };
                
                // Update cache
                emailStatusCache.set(lead.id, statusData);
                
                // Update state
                setEmailStatuses(prev => ({
                  ...prev,
                  [lead.id]: statusData
                }));
              }
            } catch (error) {
              console.error(`Error fetching email status for lead ${lead.id}:`, error);
            } finally {
              // Remove from pending requests
              pendingStatusRequests.delete(lead.id);
            }
            
            // Add a small delay between requests to avoid rate limiting
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }
        
        // Update statuses for leads with cached data
        const cachedStatuses = {};
        for (const lead of filteredLeads) {
          if (lead.id) {
            const cachedData = emailStatusCache.get(lead.id);
            if (cachedData && (now - cachedData.timestamp < STATUS_CACHE_DURATION)) {
              cachedStatuses[lead.id] = cachedData;
            }
          }
        }
        
        if (componentMounted.current && Object.keys(cachedStatuses).length > 0) {
          setEmailStatuses(prev => ({
            ...prev,
            ...cachedStatuses
          }));
        }
      } catch (error) {
        console.error('Error fetching email statuses:', error);
      } finally {
        if (componentMounted.current) {
          setIsLoadingEmails(false);
        }
      }
    };
    
    fetchEmailStatuses();
  }, [filteredLeads]);
  
  // Format state display
  const formatState = (stateCode) => {
    console.log("Formatting state code:", stateCode);
    if (!stateCode) return 'N/A';
    const stateName = US_STATES[stateCode];
    console.log("Found state name:", stateName, "for code:", stateCode);
    return stateName ? `${stateName} (${stateCode})` : stateCode;
  };

  // Calculate age from year born if available
  const calculateAge = (yearBorn, currentAge) => {
    if (yearBorn) {
      const currentYear = new Date().getFullYear();
      return currentYear - parseInt(yearBorn);
    }
    return currentAge || 'N/A';
  };

  // Email status badge
  const getEmailStatusBadge = (leadId) => {
    const status = emailStatuses[leadId];
    if (!status) return <Badge variant="default">Loading...</Badge>;
    
    if (status.purchased) {
      return (
        <div className="flex flex-col gap-1">
          <Badge variant="success">Purchased</Badge>
          {status.fullCount > 0 && (
            <Badge variant="info">Full Details Sent</Badge>
          )}
        </div>
      );
    }
    
    if (status.initialCount === 0) {
      return <Badge variant="default">No Emails</Badge>;
    }
    
    return (
      <div className="flex flex-col gap-1">
        <Badge variant="secondary">{status.initialCount} Initial Emails</Badge>
        <span className="text-xs text-gray-500">{status.uniqueAgents} agents notified</span>
      </div>
    );
  };

  const sortedLeads = [...filteredLeads].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // Special case for dates
    if (sortField === 'created_at' || sortField === 'updated_at') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    }
    
    // Special case for numbers
    if (sortField === 'investment_amount' || sortField === 'price' || sortField === 'year_born') {
      aValue = aValue ? parseFloat(aValue) : 0;
      bValue = bValue ? parseFloat(bValue) : 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedLeads.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedLeads.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Sort indicator
  const getSortIcon = (field) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc' ? '▲' : '▼';
  };

  // Handle sort
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle select all leads on current page
  const handleSelectAll = (e) => {
    const currentItems = filteredLeads.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
    currentItems.forEach(lead => {
      onSelectLead(lead.id, e.target.checked);
    });
  };

  // Check if all leads on current page are selected
  const isAllSelected = () => {
    const currentItems = filteredLeads.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
    return currentItems.length > 0 && currentItems.every(lead => selectedLeads.includes(lead.id));
  };

  return (
    <div className="bg-white shadow overflow-hidden rounded-lg">
      <div className="p-4 border-b">
        <div className="flex justify-between items-center gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search leads..."
              className="w-full p-2 border rounded-md"
            />
          </div>
          {selectedLeads.length > 0 && (
            <button
              onClick={() => onBulkDelete(selectedLeads)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 whitespace-nowrap flex-shrink-0"
            >
              Delete Selected ({selectedLeads.length})
            </button>
          )}
        </div>
      </div>
      
      {isLoadingEmails && (
        <div className="p-2 text-center text-sm text-gray-500 bg-blue-50">
          Loading email information...
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-12 px-6 py-3 text-left">
                <div className="flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected()}
                    onChange={handleSelectAll}
                    className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('id')}
              >
                ID {getSortIcon('id')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('name')}
              >
                Name {getSortIcon('name')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('email')}
              >
                Email {getSortIcon('email')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('phone')}
              >
                Phone {getSortIcon('phone')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('zip_code')}
              >
                ZIP {getSortIcon('zip_code')}
              </th>
              {/* Highlighted City Column */}
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-green-50"
                onClick={() => handleSort('city')}
              >
                City {getSortIcon('city')}
              </th>
              {/* Highlighted State Column */}
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-green-50"
                onClick={() => handleSort('state')}
              >
                State {getSortIcon('state')}
              </th>
              {/* Highlighted Year Born Column */}
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-green-50"
                onClick={() => handleSort('year_born')}
              >
                Year Born {getSortIcon('year_born')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('age')}
              >
                Age {getSortIcon('age')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('investment_amount')}
              >
                Investment {getSortIcon('investment_amount')}
              </th>
              {/* Email Status Column with info icon */}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                <div className="flex items-center space-x-1">
                  <span>Email Status</span>
                  <div className="relative group">
                    <Info className="h-4 w-4 text-gray-400 cursor-help" />
                    <div className="absolute left-0 top-full mt-2 w-64 p-3 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition z-10">
                      <p className="mb-2 font-semibold">Email Status Information</p>
                      <ul className="list-disc pl-4 space-y-1">
                        <li><strong>Initial Email:</strong> Shows if the lead has received initial notification emails</li>
                        <li><strong>Purchased:</strong> Shows if the lead has been purchased by an agent</li>
                        <li><strong>Full Details:</strong> Shows if full contact details have been sent after purchase</li>
                      </ul>
                      <p className="mt-2 italic">Click "Emails" to see full history</p>
                    </div>
                  </div>
                </div>
              </th>
              {/* New Initial Email Sent column */}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                Initial Email
              </th>
              {/* New Lead Purchased column */}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                Purchased
              </th>
              {/* New Full Details column */}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                Full Details
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('status')}
              >
                Status {getSortIcon('status')}
              </th>
              {/* Highlighted Unbounce Fields */}
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-blue-50"
                onClick={() => handleSort('unbounce_account_name')}
              >
                Unbounce Account {getSortIcon('unbounce_account_name')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-blue-50"
                onClick={() => handleSort('unbounce_page_name')}
              >
                Unbounce Page {getSortIcon('unbounce_page_name')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('created_at')}
              >
                Created {getSortIcon('created_at')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan="17" className="px-6 py-4 text-center text-gray-500">
                  No leads found
                </td>
              </tr>
            ) : (
              currentItems.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="w-12 px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead.id)}
                        onChange={(e) => onSelectLead(lead.id, e.target.checked)}
                        className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {lead.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {lead.name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lead.email || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lead.phone || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lead.zip_code || 'N/A'}
                  </td>
                  {/* City Cell with highlight */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-green-50 font-medium">
                    {lead.city || 'N/A'}
                  </td>
                  {/* State Cell with highlight */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-green-50 font-medium">
                    {formatState(lead.state)}
                  </td>
                  {/* Year Born Cell with highlight */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-green-50 font-medium">
                    {lead.year_born || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {calculateAge(lead.year_born, lead.age)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lead.investment_amount ? `$${Number(lead.investment_amount).toLocaleString()}` : 'N/A'}
                  </td>
                  {/* Email Status Cell */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center bg-blue-50">
                    {getEmailStatusBadge(lead.id)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm bg-blue-50">
                    {!emailStatuses[lead.id] ? (
                      <Badge variant="default">Loading...</Badge>
                    ) : emailStatuses[lead.id]?.initialCount > 0 ? (
                      <div className="flex flex-col">
                        <Badge variant="success">Yes</Badge>
                        <span className="text-xs text-gray-500">
                          {emailStatuses[lead.id]?.initialDate ? 
                            new Date(emailStatuses[lead.id].initialDate).toLocaleDateString() :
                            'Date Unknown'
                          }
                        </span>
                        <span className="text-xs text-gray-500">
                          {emailStatuses[lead.id]?.uniqueAgents} agents
                        </span>
                      </div>
                    ) : (
                      <Badge variant="default">No</Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm bg-blue-50">
                    {lead.is_purchased ? (
                      <div className="flex flex-col">
                        <Badge variant="success">Yes</Badge>
                        {lead.purchase_date && (
                          <span className="text-xs text-gray-500">
                            {new Date(lead.purchase_date).toLocaleDateString()}
                          </span>
                        )}
                        {lead.price && (
                          <span className="text-xs text-gray-500">
                            ${parseFloat(lead.price).toFixed(2)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <Badge variant="default">No</Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm bg-blue-50">
                    {!emailStatuses[lead.id] ? (
                      <Badge variant="default">Loading...</Badge>
                    ) : emailStatuses[lead.id]?.fullCount > 0 ? (
                      <div className="flex flex-col">
                        <Badge variant="success">Yes</Badge>
                        <span className="text-xs text-gray-500">
                          {emailStatuses[lead.id]?.fullDate ? 
                            new Date(emailStatuses[lead.id].fullDate).toLocaleDateString() :
                            'Date Unknown'
                          }
                        </span>
                      </div>
                    ) : (
                      <Badge variant="default">No</Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      lead.status === 'available' 
                        ? 'bg-green-100 text-green-800' 
                        : lead.status === 'purchased' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                    }`}>
                      {lead.status || 'available'}
                    </span>
                  </td>
                  {/* Unbounce Account Cell with highlight */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-blue-50">
                    {lead.unbounce_account_name || 'N/A'}
                  </td>
                  {/* Unbounce Page Cell with highlight */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-blue-50">
                    {lead.unbounce_page_name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onEdit(lead)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setSelectedEmailLead(lead.id)}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <Mail className="h-3 w-3 mr-1" />
                        Emails
                      </button>
                      <Link
                        to={`/admin/emails/${lead.id}`}
                        className="text-green-600 hover:text-green-900"
                      >
                        Manage
                      </Link>
                      <button
                        onClick={() => onDelete(lead.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {totalPages > 1 && (
        <div className="p-4 flex items-center justify-between border-t">
          <div className="flex-1 flex justify-between">
            <button
              onClick={() => paginate(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                currentPage === 1 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <div className="hidden md:flex">
              {[...Array(totalPages).keys()].map(number => (
                <button
                  key={number + 1}
                  onClick={() => paginate(number + 1)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium mx-1 rounded-md ${
                    currentPage === number + 1
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {number + 1}
                </button>
              ))}
            </div>
            <button
              onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                currentPage === totalPages 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {selectedEmailLead && (
        <EmailHistoryModal
          leadId={selectedEmailLead}
          leadName={getLeadName(selectedEmailLead)}
          isOpen={!!selectedEmailLead}
          onClose={() => setSelectedEmailLead(null)}
        />
      )}
    </div>
  );
};

export default LeadTable;